import { AutocompleteInputChangeReason, GridSize } from "@mui/material";
import { ResponsiveStyleValue } from "@mui/system";
import { DataGridProps } from "@mui/x-data-grid";
import { ISelectOption } from "src/shared/types/field.types";
import { TIconNames } from "src/shared/types/iconNames";
import { ECalendarRange } from "../../fields/DateRangeField/model/types";
import { IMenuItem } from "../../MenuComponent/model/types";

export enum ESortDirection {
  ASC = "ASC",
  DESC = "DESC",
}

export interface ISortValue {
  sortBy: string;
  sortDirection: ESortDirection | "";
}

export interface ISortModel {
  onSort: (v: ISortValue) => void;
  value: ISortValue;
  options: ISelectOption[];
}

export interface ISearchModel {
  onSearch: (v: string) => void;
  value: string;
}

export interface IFilterValue<T = string | number> {
  name: string;
  value: T;
  itemColor?: string;
}

export interface IFilterItem<T = string> extends IFilterValue<T> {
  type: EFilterType;
  options?: ISelectOption<string | number>[];
  title: string;
  width?: ResponsiveStyleValue<GridSize>;
  hideClearButton?: boolean;
  loading?: boolean;
  placeholder?: string;
  onInputChange?: (
    event: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => void;
  onChange?: (arg: string) => void;
}

export interface ICalendarFilterInfo {
  startDate: Date | null;
  endDate: Date | null;
  calendarRange: ECalendarRange | null;
}

export interface IFilterCalendarItem {
  name: string;
  title: string;
  type: EFilterCalendarType.CALENDAR;
  value: ICalendarFilterInfo;
  minDate?: Date | null;
  maxDate?: Date | null;
  hideClearButton?: boolean;
}

export interface IFilterMonthItem {
  name: string;
  title: string;
  type: EFilterCalendarType.MONTH;
  value: string | null; // "YYYY-MM"
  hideClearButton?: boolean;
}

export type TFilterPossibleValues =
  | string
  | number
  | boolean
  | (string | number | boolean)[]
  | null;

export interface IFilterModel {
  onFilter: (v: IFilterValue<TFilterPossibleValues>[]) => void;
  onReset?: () => void;
  filters: (
    | IFilterItem<TFilterPossibleValues>
    | IFilterCalendarItem
    | IFilterMonthItem
  )[];
  extendedFilters?: IFilterItem<TFilterPossibleValues>[];
}

export enum EFilterType {
  SINGLE_SELECT = "SINGLE_SELECT",
  MULTI_SELECT = "MULTI_SELECT",
  BOOLEAN = "BOOLEAN",
  STRING = "STRING",
  SINGLE_AUTOCOMPLETE = "SINGLE_AUTOCOMPLETE",
  MULTI_AUTOCOMPLETE = "MULTI_AUTOCOMPLETE",
}

export enum EFilterCalendarType {
  CALENDAR = "CALENDAR",
  MONTH = "MONTH",
}

export interface ITableMenuModel {
  iconName?: TIconNames;
  uuid: string;
  menuItems: IMenuItem[];
  isPrimaryColor?: boolean;
}

export interface ITableComponentProps extends DataGridProps {
  pageSizeOptions?: number[];
  hideHeader?: boolean;
  hidePagination?: boolean;
}

declare module "@mui/x-data-grid/models" {
  interface PaginationPropsOverrides {
    paginationModel: { page: number; pageSize: number } | undefined;
    pageSizeOptions: number[];
    hidePagination?: boolean;
    total: number;
  }
}

export type TMultiSortDir = "asc" | "desc";
export interface IMultiSortItem {
  sortBy: string;
  sortDirection: TMultiSortDir;
}

export interface IMultiSortModel {
  mode: "multi";
  value: IMultiSortItem[];
  onChange: (next: IMultiSortItem[]) => void;
  options: ISelectOption[];
}

export type TTableSortModel = ISortModel | IMultiSortModel;
